import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Drop, Path as PathIcon, Tent, Eye, MapPin } from '@phosphor-icons/react';

export const WAYPOINT_CATEGORIES = [
  { id: 'water', label: 'Water', Icon: Drop, color: '#118AB2' },
  { id: 'fork', label: 'Fork', Icon: PathIcon, color: '#FFD166' },
  { id: 'camp', label: 'Camp', Icon: Tent, color: '#06D6A0' },
  { id: 'view', label: 'View', Icon: Eye, color: '#FF9F1C' },
  { id: 'other', label: 'Other', Icon: MapPin, color: '#A1A1AA' },
];

export default function WaypointDialog({ open, onOpenChange, onSave, currentPosition }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('water');

  const save = () => {
    if (!currentPosition) return;
    onSave({
      id: `wp-${Date.now()}`,
      name: name.trim() || defaultName(category),
      category,
      lat: currentPosition.lat,
      lng: currentPosition.lng,
      t: Date.now(),
    });
    setName('');
    setCategory('water');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="waypoint-dialog" className="bg-[#141414] border-white/10 max-w-[92%] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Drop a waypoint</DialogTitle>
          <DialogDescription>Pin your current location so you can find it again.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Name</label>
            <Input
              data-testid="waypoint-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={defaultName(category)}
              className="mt-1 bg-black/40 border-white/10 h-11 text-base"
              autoFocus
              maxLength={40}
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Category</div>
            <div className="grid grid-cols-5 gap-2">
              {WAYPOINT_CATEGORIES.map(({ id, label, Icon, color }) => (
                <button
                  key={id}
                  data-testid={`waypoint-cat-${id}`}
                  onClick={() => setCategory(id)}
                  className={`btn-tap flex flex-col items-center gap-1 py-2 rounded-lg border ${
                    category === id ? 'bg-white/10 border-white/20' : 'bg-black/30 border-white/5'
                  }`}
                >
                  <Icon size={20} weight="fill" color={color} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-300">{label}</span>
                </button>
              ))}
            </div>
          </div>
          {currentPosition && (
            <div className="text-[11px] text-zinc-500 font-mono-stat">
              {currentPosition.lat.toFixed(5)}, {currentPosition.lng.toFixed(5)}
            </div>
          )}
        </div>

        <DialogFooter className="sm:flex-row sm:justify-end flex-row justify-end gap-2">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="waypoint-cancel"
            className="btn-tap px-4 h-11 rounded-lg bg-white/5 border border-white/10 font-semibold text-sm"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!currentPosition}
            data-testid="waypoint-save"
            className="btn-tap px-5 h-11 rounded-lg bg-[#FF9F1C] text-black font-display font-bold disabled:opacity-40"
          >
            Drop pin
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultName(cat) {
  const map = { water: 'Water source', fork: 'Trail fork', camp: 'Camp spot', view: 'Viewpoint', other: 'Waypoint' };
  return map[cat] || 'Waypoint';
}

export function categoryMeta(id) {
  return WAYPOINT_CATEGORIES.find((c) => c.id === id) || WAYPOINT_CATEGORIES[4];
}
